import {
  CSSProperties,
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactPlayer from 'react-player/lazy';
import cn from 'classnames';
import Slider from '@material-ui/core/Slider';
import { DEFAULT_TIME_HMS, isNumber, useMediaQuery } from 'common/utils';
import Icon from 'components/common/Icon';
import { secondsToHms } from 'common/utils';
import styles from './ViewLectureVideoPlayer.module.scss';
// import useGlobalStore from 'store/global';
import { useMemo } from 'react';
import {
  increaseLectureViewCount,
  LecturePaidType,
  LectureType,
} from 'service/course';
import Image from 'next/image';
import classNames from 'classnames';
import Button from 'components/common/Button';
import LinkButton from 'components/common/LinkButton';
import { CourseDetailsContext } from '..';
import PlayerRateOptions from './PlayerRateOptions';
import useFullscreen from '@rooks/use-fullscreen';
import useFullscreenStatus from 'common/utils/fullscreen';
import useStore from 'store/timeline';

export enum AutoplayThreshold {
  Center,
  Top,
}

export default function ViewLectureVideoPlayer({
  lecture,
  isPreview = false,
}: {
  lecture: LectureType;
  isPreview?: boolean;
}): JSX.Element {
  // const setSnackbarMessage = useGlobalStore(
  //   (state) => state.setSnackbarMessage
  // );
  const timebarRef = useRef<HTMLDivElement>(null);
  const controlMediaRef = useRef<HTMLDivElement>(null);
  const [activeAudioPlayer, setActiveAudioPlayer] = useStore((state) => [
    state.activeAudioPlayer,
    state.setActiveAudioPlayer,
  ]);
  const {
    setOpenQuestionAnswerDialog,
    courseInfo,
    setCurrentLecture,
    viewdLectureIds,
    setViewedLectureIds,
    setOpenCoursePaywall,
  } = useContext(CourseDetailsContext);
  const { toggle: toggleFullscreen, isFullscreen } = useFullscreen();
  const isMobileScreen = useMediaQuery('(max-width: 767px)');
  const [isMounted, setIsMounted] = useState(false);
  const playerRef = useRef<ReactPlayer>(null);
  const playerWrapperRef = useRef<HTMLDivElement>(null);
  const [muted, setMuted] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [timebarVal, setTimebarVal] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasEnded, setHasEnded] = useState(false);
  const [lastClickTime, setLastClickTime] = useState(-1);
  const [isReady, setIsReady] = useState(false);
  const [isFullScreenBrowser] = useFullscreenStatus();
  const increaseViewCount = useCallback(() => {
    if (isPreview) return;
    increaseLectureViewCount({
      courseId: courseInfo.id,
      lectureId: lecture.id,
    });
    setViewedLectureIds((ids) => {
      if (ids.includes(lecture.id)) return ids;
      return [...ids, lecture.id];
    });
  }, [courseInfo.id, isPreview, lecture.id, setViewedLectureIds]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Stop playing audio when other audio sources starts playing
  // Note: how is this working?? why don't playing and muted become stale values??
  useEffect(() => {
    if (activeAudioPlayer?.playing && playing && !muted) {
      setMuted(true);
    }
  }, [activeAudioPlayer, playing, muted]);

  const currentLectureIndex = useMemo(() => {
    if (!lecture) return -1;
    return (courseInfo.lectures || []).findIndex(
      (lec) => lec.id === lecture?.id
    );
  }, [courseInfo, lecture]);

  const lectureVideoUrl = useMemo(() => {
    return `${lecture.lecture_media.url}?token=${lecture.token}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lecture.id]);

  // Rewind and fast forward with
  // - positivie skipTime means fast forward
  // - negative skipTime means rewind
  const handleTimeSkip = useCallback(
    (skipTime: number) => {
      if (!playerRef.current) return;
      if (skipTime < 0 && !isPreview) {
        increaseViewCount();
      }
      let newTime = playedSeconds + skipTime;
      if (newTime > duration) newTime = duration - 1;
      if (newTime < 0) newTime = 0;
      setPlayedSeconds(newTime);
      playerRef.current.seekTo(newTime, 'seconds');
      setTimeout(() => {
        if (newTime === duration) {
          setHasEnded(true);
        }
      }, 500);
    },
    [duration, increaseViewCount, isPreview, playedSeconds]
  );

  // Media control overlay
  const mediaControlBtn = useMemo(() => {
    let btn: JSX.Element;
    if (hasEnded) {
      btn = <></>;
    } else if (!playing) {
      btn = (
        <div className={styles.playWrapper}>
          <div className={styles.skipBtn}>
            <Icon
              name="play-rewind"
              height={50}
              width={50}
              onClick={() => handleTimeSkip(-15)}
            />
          </div>
          <div className={styles.centerBtn}>
            <Icon
              name="play"
              width={96}
              height={96}
              onClick={() => setPlaying(true)}
            />
          </div>
          <div className={styles.skipBtn}>
            <Icon
              name="play-forward"
              height={50}
              width={50}
              onClick={() => handleTimeSkip(15)}
            />
          </div>
        </div>
      );
    } else if (playing) {
      btn = (
        <div className={cn(styles.playWrapper, styles.pauseBtn)}>
          <div className={styles.skipBtn}>
            <Icon
              name="play-rewind"
              height={50}
              width={50}
              onClick={() => handleTimeSkip(-15)}
            />
          </div>
          <div className={styles.centerBtn}>
            <Icon
              name="pause"
              width={96}
              height={96}
              onClick={() => setPlaying(false)}
            />
          </div>
          <div className={styles.skipBtn}>
            <Icon
              name="play-forward"
              height={50}
              width={50}
              onClick={() => handleTimeSkip(15)}
            />
          </div>
        </div>
      );
    }
    return btn;
  }, [handleTimeSkip, hasEnded, playing]);

  const handleSpeakerBtnClick: MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      setMuted((muted) => !muted);
      if (playing && muted) {
        setActiveAudioPlayer('lecture', false);
      }
    },
    [muted, playing, setActiveAudioPlayer]
  );

  const handleClickFullscreen: MouseEventHandler = (e) => {
    e.stopPropagation();
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (document.exitFullscreen && (document as any).mozFullScreen) {
        void document.exitFullscreen();
      }
      // eslint-disable-next-line no-empty
    } catch (error) {}

    if (!isMobileScreen) {
      void toggleFullscreen(playerWrapperRef.current);
      return;
    }
    /* eslint-disable @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    const elem = (playerRef.current?.wrapper as HTMLDivElement)?.querySelector(
      'video'
    );
    // @ts-ignore
    if (elem && elem.webkitEnterFullScreen) {
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      elem.webkitEnterFullScreen();
    } else void toggleFullscreen(playerWrapperRef.current);
    /* eslint-enable @typescript-eslint/ban-ts-comment */
  };

  // Volume
  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, newVolume: number | number[]) => {
      e.stopPropagation();
      if (!isNumber(newVolume) || Array.isArray(newVolume)) {
        newVolume = 0.5;
      }
      if (newVolume < 0.025) {
        setMuted(true);
      } else {
        setMuted(false);
        setVolume(Number(newVolume));
        if (playing) {
          setActiveAudioPlayer('lecture', false);
        }
      }
    },
    [playing, setActiveAudioPlayer]
  );

  // Timebar
  const handleTimebarChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (!playerRef.current) return;
      const newTimebarVal = Number(e.target.value);
      playerRef.current.seekTo(newTimebarVal, 'fraction');
      setTimebarVal(newTimebarVal);
      setPlayedSeconds(newTimebarVal * duration);
      setTimeout(() => {
        // setPlaying(true);
        if (newTimebarVal === 1) {
          setHasEnded(true);
        }
      }, 500);
    },
    [duration]
  );

  useEffect(() => {
    setTimebarVal(duration === 0 ? 0 : playedSeconds / duration);
  }, [playedSeconds, setTimebarVal, duration]);

  const handleChangeChapter = (position: number) => {
    if (!playerRef.current) return;
    playerRef.current.seekTo(position, 'seconds');
    increaseViewCount();
    setTimeout(() => {
      if (position >= duration) {
        setHasEnded(true);
      }
    }, 500);
  };
  const stopPropagation: MouseEventHandler = (e) => {
    e.stopPropagation();
  };

  const handleOverlayClick: MouseEventHandler = (e) => {
    stopPropagation(e);
    const selection = window.getSelection();
    if (
      selection.type != 'Range' &&
      !timebarRef?.current?.contains(e.target as HTMLElement) &&
      !controlMediaRef?.current?.contains(e.target as HTMLElement)
    ) {
      setPlaying((playing) => !playing);
      if (!playing) {
        increaseViewCount();
        setActiveAudioPlayer('lecture', false);
      }
    }
  };

  const hms = useMemo(() => {
    return duration > 0 && duration >= playedSeconds
      ? secondsToHms(Math.round(playedSeconds))
      : DEFAULT_TIME_HMS;
  }, [duration, playedSeconds]);

  const totalHms = useMemo(() => {
    return secondsToHms(Math.round(duration));
  }, [duration]);

  const reactPlayerStyle = useMemo(() => {
    let style: CSSProperties;
    if (isFullscreen || isFullScreenBrowser) {
      style = {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      };
    } else {
      style = {};
    }
    return style;
  }, [isFullScreenBrowser, isFullscreen]);

  const closeFullScreen = () => {
    if (isFullscreen || isFullScreenBrowser) {
      if (
        document.exitFullscreen &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (document as any).mozFullScreen
      ) {
        void document.exitFullscreen();
        return;
      }
      void toggleFullscreen();
    }
  };
  return (
    <>
      <div
        ref={playerWrapperRef}
        className={cn(styles.playerWrapper, {
          [styles.fullscreenVideo]: isFullscreen || isFullScreenBrowser,
        })}
      >
        <ReactPlayer
          width="100%"
          height="100%"
          style={reactPlayerStyle}
          ref={playerRef}
          url={lectureVideoUrl}
          muted={muted}
          volume={volume}
          playsinline
          playing={playing && isReady && isMounted}
          onError={() => {
            if (!isReady) return;
            setTimeout(() => {
              if (playerRef.current && playedSeconds < 10) {
                playerRef.current.seekTo(playedSeconds + 2);
              }
            }, 500);
          }}
          onReady={() => {
            setIsReady(true);
            if (!playing) setPlaying(true);
            if (!viewdLectureIds.includes(lecture.id)) {
              increaseViewCount();
            }
          }}
          onProgress={({ playedSeconds }) => {
            setPlayedSeconds(playedSeconds);
            if (playedSeconds < duration - 1 && hasEnded) {
              setHasEnded(false);
            }
          }}
          onDuration={(duration) => setDuration(duration)}
          onPlay={() => {
            setHasEnded(false);
          }}
          playbackRate={playbackRate}
          onEnded={() => {
            setHasEnded(true);
            setPlaying(false);
          }}
          config={{
            file: {
              attributes: {
                poster: lecture.lecture_thumbnail_media.url,
              },
            },
          }}
        ></ReactPlayer>
        {hasEnded ? (
          <div className={styles.answerButtonOverlay}>
            <div className={styles.endOfLectureTitle}>
              レクチャーが終了しました
            </div>
            {lecture.questions?.length > 0 && (
              <div className={styles.answerDescription}>
                クリエイターのクイズに解答しよう！
              </div>
            )}
            {lecture.questions?.length > 0 && (
              <div className={styles.answerButton}>
                <Button
                  text={
                    currentLectureIndex < courseInfo.lectures.length - 1
                      ? 'クイズに解答して次のレクチャーへ'
                      : 'クイズに解答する'
                  }
                  onClick={() => {
                    setOpenQuestionAnswerDialog(true);
                    closeFullScreen();
                  }}
                />
              </div>
            )}

            {lecture.questions?.length === 0 &&
              currentLectureIndex < courseInfo.lectures.length - 1 && (
                <div className={styles.answerButton}>
                  <Button
                    text="次のレクチャーへ"
                    onClick={() => {
                      if (currentLectureIndex >= courseInfo.lectures.length - 1)
                        return;
                      const newLectureIndex = currentLectureIndex + 1;
                      if (isPreview) {
                        setCurrentLecture(
                          courseInfo.lectures[currentLectureIndex + 1]
                        );
                        return;
                      }
                      const newLecture = courseInfo.lectures[newLectureIndex];
                      if (newLecture.type === LecturePaidType.Free) {
                        setCurrentLecture(newLecture);
                        return;
                      }
                      // if can watch
                      if (courseInfo.is_owner || courseInfo.is_purchased) {
                        setCurrentLecture(newLecture);
                      } else {
                        // if can't watch => open paywall
                        closeFullScreen();
                        setOpenCoursePaywall && setOpenCoursePaywall(true);
                        return;
                      }
                    }}
                  />
                </div>
              )}
            <div className={styles.rewindButton}>
              <LinkButton
                text="もう一度動画を見る"
                onClick={() => {
                  setPlaying(true);
                  increaseViewCount();
                }}
              />
            </div>
          </div>
        ) : (
          <div
            onClick={handleOverlayClick}
            className={cn(styles.playerOverlay, {
              [styles.fullscreen]: isFullscreen || isFullScreenBrowser,
            })}
          >
            <div className={styles.mediaControlBtn} ref={controlMediaRef}>
              {mediaControlBtn}
            </div>
            <div
              className={cn(styles.leftCorner, {
                [styles.mobileCtrlLeftCorner]: isMobileScreen,
              })}
            >
              {duration > 0 && (
                <div className={styles.playTime}>
                  {hms} / {totalHms}
                </div>
              )}
              <div className={styles.timebar} ref={timebarRef}>
                <input
                  key={`video-time-slider-${lecture.lecture_thumbnail_media.media_id}`}
                  type="range"
                  min={0}
                  max={1}
                  step={0.025}
                  value={timebarVal}
                  style={{
                    background: `linear-gradient(to right, #fff 0%, #fff ${
                      timebarVal * 100
                    }%, #aeaeae ${timebarVal * 100}%, #aeaeae 100%)`,
                  }}
                  onChange={handleTimebarChange}
                  className={styles.sliderTime}
                  onMouseDown={() => {
                    setLastClickTime(timebarVal);
                  }}
                  onMouseUp={() => {
                    // call event increase view count
                    if (lastClickTime > timebarVal) {
                      increaseViewCount();
                    }
                    setLastClickTime(-1);
                  }}
                />
              </div>
            </div>
            <div className={styles.rightCorner}>
              <div className={styles.volumeCtrl} onClick={stopPropagation}>
                {!isMobileScreen && (
                  <Slider
                    key={`video-slider-${lecture.lecture_media.media_id}`}
                    orientation="vertical"
                    defaultValue={1}
                    min={0}
                    max={1}
                    step={0.025}
                    onChange={handleVolumeChange}
                    className={styles.volumeSlider}
                  />
                )}
                <div
                  className={styles.speakerBtn}
                  onClick={handleSpeakerBtnClick}
                >
                  <Icon
                    name="speaker"
                    width={26}
                    height={26}
                    hasOn
                    isOn={!muted}
                  />
                </div>
              </div>
              <PlayerRateOptions
                setPlaybackRate={setPlaybackRate}
                playbackRate={playbackRate}
              />
              <div
                className={styles.fullscreenBtn}
                onClick={handleClickFullscreen}
              >
                <div>
                  <Icon name="fullscreen" width={17} height={17} />
                </div>
                {!isMobileScreen && (
                  <div className={styles.fullscreenText}>
                    {isFullscreen ? '全画面を閉じる' : '全画面で見る'}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <ChaptersList
        lecture={lecture}
        handleChangeChapter={handleChangeChapter}
        currentPosition={playedSeconds}
      />
    </>
  );
}

const ChaptersList = ({
  lecture,
  handleChangeChapter,
  currentPosition,
}: {
  lecture: LectureType;
  handleChangeChapter: (position: number) => void;
  currentPosition?: number;
}) => {
  const showNextBack = lecture.chapters.length > 3;
  const chaptersListRef = useRef<HTMLDivElement>();
  const { setOpenQuestionAnswerDialog } = useContext(CourseDetailsContext);
  const scrollShowMore = (offset: number) => {
    if (chaptersListRef.current) {
      chaptersListRef.current.scrollLeft += offset;
    }
  };
  if (lecture.chapters.length === 0 && lecture.questions?.length === 0)
    return <></>;
  return (
    <div className={styles.chaptersListWrapper}>
      <div className={styles.chaptersListHeader}>
        チャプター({lecture.chapters.length})
        {lecture.questions.length > 0 &&
          `・クイズ(${lecture.questions.length})`}
      </div>
      <div className={styles.chaptersList} ref={chaptersListRef}>
        {showNextBack && (
          <div
            className={styles.backBtn}
            onClick={() => {
              scrollShowMore(-300);
            }}
          >
            <Icon name="foward-arrow" width={24} height={24} />
          </div>
        )}
        {showNextBack && (
          <div
            className={styles.nextBtn}
            onClick={() => {
              scrollShowMore(300);
            }}
          >
            <Icon name="foward-arrow" width={24} height={24} />
          </div>
        )}

        {(lecture.chapters || []).map((chapter, index) => {
          const isCurrentPosition =
            currentPosition >= chapter.start_position &&
            (index === lecture.chapters.length - 1 ||
              currentPosition < lecture.chapters[index + 1]?.start_position);
          return (
            <div
              className={classNames(styles.chapterItem, {
                [styles.currentItem]: isCurrentPosition,
              })}
              onClick={() => handleChangeChapter(chapter.start_position)}
              key={`${chapter.id}-${index}`}
            >
              <div className={styles.chapterThumbnail}>
                {chapter.thumbnail_media ? (
                  <Image
                    src={chapter.thumbnail_media.url}
                    height={chapter.thumbnail_media?.height}
                    width={chapter.thumbnail_media?.width}
                    alt={chapter.detail || ''}
                  />
                ) : (
                  <Image
                    src={lecture.lecture_thumbnail_media?.url}
                    height={lecture.lecture_thumbnail_media?.height}
                    width={lecture.lecture_thumbnail_media?.width}
                    alt={chapter.detail || ''}
                  />
                )}
              </div>
              <span className={styles.chapterStartPosition}>
                {secondsToHms(chapter.start_position)}
              </span>
              <div className={styles.chapterTitle}>{chapter.detail}</div>
            </div>
          );
        })}
        {/* Question */}
        {lecture.questions && lecture.questions.length > 0 && (
          <div
            className={styles.chapterItem}
            onClick={() => setOpenQuestionAnswerDialog(true)}
          >
            <div className={styles.chapterThumbnail}>
              <Icon name="quiz" width={72} height={72} />
            </div>
            <span className={styles.chapterStartPosition}>クイズ</span>
            <div className={styles.chapterTitle}></div>
          </div>
        )}
      </div>
    </div>
  );
};
